import { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { sortBy } from 'lodash';
import useRoiAppointment from 'hooks/useRoiAppointment';
import PeriodComparison from 'pages/OnlineSchedulingReportPage/PeriodComparison/PeriodComparison';
import {
  LABEL_FORMAT_BY_GRANULARITY,
  bucketTimeSeries,
  getGranularityForRange,
} from 'utils/bucketTimeSeries';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

interface TotalAppointmentRequestChartProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  status: string | undefined;
  clinicIds?: string[];
}

const TotalAppointmentRequestChart: FC<TotalAppointmentRequestChartProps> = ({
  selectedDateRange,
  hourType,
  status,
  clinicIds,
}) => {
  const { data: roiAppointment, isLoading: isRoiLoading } = useRoiAppointment({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    status,
    clinicIds,
  });

  const { chartData, bucketed, granularity, averageProduction } =
    useMemo(() => {
      const dailyDetails = roiAppointment?.periodData?.details
        ? sortBy(roiAppointment.periodData.details, 'date')
        : [];
      const resolvedGranularity = getGranularityForRange(
        selectedDateRange.fromDate,
        selectedDateRange.toDate
      );
      const bucketedEntries = bucketTimeSeries(
        dailyDetails,
        resolvedGranularity,
        ['totalAppointments']
      );
      const labelFormat = LABEL_FORMAT_BY_GRANULARITY[resolvedGranularity];

      return {
        bucketed: bucketedEntries,
        granularity: resolvedGranularity,
        averageProduction: dailyDetails.length
          ? Math.round(
              dailyDetails.reduce((sum, d) => sum + d.totalAppointments, 0) /
                dailyDetails.length
            )
          : 0,
        chartData: {
          labels: bucketedEntries.map((d) =>
            moment(d.date).format(labelFormat)
          ),
          datasets: [
            {
              fill: true,
              data: bucketedEntries.map((d) => d.totalAppointments),
              borderColor: '#B01BAE',
              backgroundColor: '#f9eaf5',
              tension: 0,
              pointBackgroundColor: '#B01BAE',
            },
          ],
        },
      };
    }, [roiAppointment, selectedDateRange.fromDate, selectedDateRange.toDate]);

  const currentTotal = roiAppointment?.periodData?.totalAppointments ?? 0;
  const lastTotal = roiAppointment?.lastPeriodData?.totalAppointments ?? 0;

  if (isRoiLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="flex gap-x-0.6 font-bold items-baseline">
          <span className="text-30 text-magenta">
            {(
              roiAppointment?.periodData.totalAppointments ?? 0
            ).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-x-1.6 text-14 text-darkest-grey">
          <span>
            <span className="font-bold">
              {averageProduction.toLocaleString()}
            </span>{' '}
            avg / day
          </span>
          <PeriodComparison current={currentTotal} last={lastTotal} />
        </div>
      </div>

      <div className="pr-2.3">
        <Line
          data={chartData}
          className="max-h-[30rem]"
          options={{
            plugins: {
              title: { display: false },
              subtitle: { display: false },
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const index = items[0]?.dataIndex ?? 0;
                    const entry = bucketed[index];
                    if (granularity === 'day' && entry) {
                      return moment(entry.date).format('YYYY/MM/DD');
                    }
                    return items[0]?.label ?? '';
                  },
                  label: (ctx) => `${Number(ctx.parsed.y).toLocaleString()}`,
                },
              },
            },
            scales: {
              y: {
                min: 0,
                ticks: {
                  callback: (value) =>
                    Number.isInteger(Number(value))
                      ? Number(value).toLocaleString()
                      : null,
                },
              },
            },
          }}
        />
      </div>
    </>
  );
};

export default TotalAppointmentRequestChart;
